﻿import { useEffect } from "react";
import { formatNumber } from "../../../utilities/format";
import i18n from "../../../i18n";
import { serverTerm } from "../../../i18n/serverTerms";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Divider } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useQuery } from '@tanstack/react-query';
import { faTruck } from "@fortawesome/free-solid-svg-icons";

import { MaterialMovementCardSectionDTO, MaterialMovementCurrentLocationDTO } from "../../../DTOs/Dashboards/MaterialMovementCardDataDTO";
import { materialMovementQuery } from "./dashboardQueries";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import useShiftTimes from "../../Hooks/useShiftTimes";
import useShiftLockInfo from "../../Hooks/useShiftLockInfo";
import { isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";
import { AccessLink } from "../../AccessLink/AccessLink";
import { LinkTarget } from "../../../utilities/helpers/linkTarget";
import {
  MATERIAL_MOVEMENT_TARGETS,
  drillingRigTarget,
  productionPatternTarget,
} from "../../../utilities/helpers/dashboardLinks";

import { shiftNameToLetter } from "../../../types/Shift";

export const MATERIAL_MOVEMENT_MIN_WIDTH = "min-w-[520px]";

export const MaterialMovementCard: React.FC = () => {
  const { selectedDate, selectedShift } = useShiftTimes();
  const { shiftLockState } = useShiftLockInfo(selectedDate, selectedShift);
  const { isFullScreen, onScreen, reportLive } = useDashboardCard();
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid && !!selectedShift;
  const { data = null, isLoading } = useQuery({
    ...materialMovementQuery(selectedDate, selectedShift),
    enabled: queryEnabled,
    subscribed: onScreen,
  });

  const showSkeleton = isLoading || !queryEnabled;
  const isEmpty = !showSkeleton && !data;
  const hasAfternoon = [data?.production, data?.inPit, data?.rehandle].some(section => section?.rows.some(row => (row.afternoon ?? 0) !== 0))
    || (data?.totalMaterialMovement?.afternoon ?? 0) !== 0;

  useEffect(() => {
    const current = isCurrentShift(selectedDate, shiftNameToLetter(selectedShift), shiftLockState);

    reportLive({
      current,
      tooltipText: current ? i18n.t("dashboard:live.latest") : i18n.t("dashboard:live.selected"),
    });
  }, [reportLive, selectedDate, selectedShift, shiftLockState]);

  return (
    <div className={`h-full w-full ${isFullScreen ? "md:max-w-[1520px]" : ""}`}>
      <Paper className={`!shadow-none !border-none !bg-theme-surface-2 !w-full text-center w-full h-full !rounded-none overflow-y-auto ${isFullScreen ? "md:p-3" : "p-3"}`}>
        <div className={`transition-all duration-300 ${showSkeleton ? `opacity-100 ${MATERIAL_MOVEMENT_MIN_WIDTH}` : "opacity-0"}`}>
          {showSkeleton && (
            <>
              <Skeleton variant="rectangular" animation="wave" height={28} width="100%" className={`!mb-0 py-4 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <TableRow className="bg-theme-surface-2" key={index}>
                        <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow>
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <TableRow className="bg-theme-surface-2" key={index}>
                        <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className={`!py-1 !w-full !max-w-[40%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[30%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                      <TableCell className={`!py-1 !w-full !max-w-[10%] ${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <Skeleton animation="wave" variant="rectangular" height={28} width="100%" className={`!mb-0 !bg-theme-surface ${isFullScreen ? "md:!py-6" : ""}`} />
              <TableContainer>
                <Table size="small" className="my-0">
                  <TableHead>
                    <TableRow className="bg-theme-surface-2">
                      <TableCell className="!p-1"><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                      <TableCell className="!p-1"><Skeleton animation="wave" variant="text" className="!bg-theme-table-heading" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, rowIndex) => (
                      <TableRow className="bg-theme-surface-2" key={rowIndex}>
                        {[...Array(2)].map((_, colIndex) => (
                          <TableCell key={colIndex} className={`!py-1${isFullScreen ? "md:!py-2" : ""}`}><Skeleton animation="wave" variant="text" className="!bg-theme-table-row-even" /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
        {isEmpty && (
          <DashboardCardEmptyState
            isFullScreen={isFullScreen}
            icon={faTruck}
            title={isOnline ? i18n.t("dashboard:material.emptyTitle") : i18n.t("table.offlineTitle")}
            subtitle={isOnline
              ? i18n.t("dashboard:material.empty")
              : i18n.t("dashboard:material.offline")}
          />
        )}
        {!isEmpty && (
          <div className={`transition-all ${MATERIAL_MOVEMENT_MIN_WIDTH} ${!showSkeleton ? "opacity-100 fade-up" : "opacity-0"}`}>
            <Section section={data?.production} isCardFullScreen={isFullScreen} target={MATERIAL_MOVEMENT_TARGETS.production} showAfternoon={hasAfternoon} />
            <Section section={data?.inPit} isCardFullScreen={isFullScreen} target={MATERIAL_MOVEMENT_TARGETS.inPit} showAfternoon={hasAfternoon} />
            <Section section={data?.rehandle} isCardFullScreen={isFullScreen} target={MATERIAL_MOVEMENT_TARGETS.rehandle} showAfternoon={hasAfternoon} />

            <div className="flex font-bold border-y border-theme-table-border bg-theme-table-row-even mt-2 mb-3 text-theme-table-row-contrast">
              <span
                title={i18n.t("dashboard:material.total")}
                className={`flex-shrink-0 basis-[40%] max-w-[40%] text-right px-1 border-x border-theme-table-border truncate text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {i18n.t("dashboard:material.total")}
              </span>
              <span
                className={`flex-shrink-0 basis-[20%] max-w-[20%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {serverTerm(data?.totalMaterialMovement?.measurement)}
              </span>
              <span
                className={`flex-shrink-0 ${hasAfternoon ? "basis-[10%] max-w-[10%]" : "basis-[20%] max-w-[20%]"} text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {formatNumber(data?.totalMaterialMovement?.day)}
              </span>
              {hasAfternoon && (
                <span
                  className={`flex-shrink-0 basis-[10%] max-w-[10%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
                >
                  {formatNumber(data?.totalMaterialMovement?.afternoon)}
                </span>
              )}
              <span
                className={`flex-shrink-0 basis-[10%] max-w-[10%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {formatNumber(data?.totalMaterialMovement?.night)}
              </span>
              <span
                className={`flex-shrink-0 basis-[10%] max-w-[10%] text-right px-1 border-r border-theme-table-border text-sm md:text-base ${isFullScreen ? "md:text-lg" : ""}`}
              >
                {formatNumber(data?.totalMaterialMovement?.both)}
              </span>
            </div>

            <Section section={data?.crushing} isCardFullScreen={isFullScreen} target={MATERIAL_MOVEMENT_TARGETS.crushing} showAfternoon={true} />

            <Divider className="!mt-3 !mb-2" />

            <Typography
              variant="subtitle1"
              className={`bg-theme-primary text-theme-primary-contrast border border-theme-table-heading-border !font-bold !mt-4 !mb-0 ${isFullScreen ? "md:!text-2xl md:!py-2" : ""
                }`}
            >
              {i18n.t("dashboard:material.excavatorLocations")}
            </Typography>

            <div className="flex font-bold border-b border-theme-table-heading-border bg-theme-table-heading text-theme-table-heading-contrast ">
              <span
                title={i18n.t("dashboard:material.plant")}
                className={`flex-1 text-left border-x border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {i18n.t("dashboard:material.plant")}
              </span>
              <span
                title={serverTerm(data?.currentProductionExcavatorLocations.locationLabel)}
                className={`flex-1 text-left border-r border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {serverTerm(data?.currentProductionExcavatorLocations.locationLabel)}
              </span>
            </div>

            {data?.currentProductionExcavatorLocations.locations.map((loc, i) => (
              <LocationRow
                key={i}
                location={loc}
                isCardFullScreen={isFullScreen}
                target={productionPatternTarget(loc.diggingPatternId)}
              />
            ))}

            <Typography
              variant="subtitle1"
              className={`font-semibold bg-theme-primary text-theme-primary-contrast border border-theme-table-heading-border !font-bold !mb-0 ${isFullScreen ? "md:!text-2xl md:!py-2" : ""
                }`}
            >
              {i18n.t("dashboard:material.drillLocations")}
            </Typography>

            <div className="flex font-bold border-b border-theme-table-heading-border text-theme-table-heading-contrast bg-theme-table-heading">
              <span
                title={i18n.t("dashboard:material.plant")}
                className={`flex-1 text-left border-x border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {i18n.t("dashboard:material.plant")}
              </span>
              <span
                title={serverTerm(data?.currentBlastholeDrillLocations.locationLabel)}
                className={`flex-1 text-left border-r border-theme-table-heading-border px-1 truncate text-sm md:text-base ${isFullScreen ? "md:text-xl" : ""
                  }`}
              >
                {serverTerm(data?.currentBlastholeDrillLocations.locationLabel)}
              </span>
            </div>

            {data?.currentBlastholeDrillLocations.locations.map((loc, i) => (
              <LocationRow
                key={i}
                location={loc}
                isCardFullScreen={isFullScreen}
                target={drillingRigTarget(loc.plantId)}
              />
            ))}
          </div>
        )}
      </Paper>
    </div>
  );
};

const ROW_LINK_CLASSES = "cursor-pointer hover:bg-theme-table-heading hover:text-theme-table-heading-contrast";

const LocationRow: React.FC<{
  location: MaterialMovementCurrentLocationDTO;
  isCardFullScreen?: boolean;
  target: LinkTarget | null;
}> = ({ location, isCardFullScreen, target }) => {
  const cells = (
    <>
      <span
        title={location.plant}
        className={`flex-1 px-1 border-x border-theme-table-border truncate text-left ${isCardFullScreen ? "md:text-lg" : ""}`}
      >
        {location.plant}
      </span>
      <span
        title={location.locationValue}
        className={`flex-1 px-1 truncate border-r border-theme-table-border text-left ${isCardFullScreen ? "md:text-lg" : ""}`}
      >
        {location.locationValue}
      </span>
    </>
  );

  const rowClasses = "flex border-b border-theme-table-border bg-theme-table-row-even text-theme-table-row-contrast text-sm md:text-base transition-colors";

  return (
    <AccessLink target={target} className={rowClasses} linkedClassName={ROW_LINK_CLASSES}>
      {cells}
    </AccessLink>
  );
};

const Section: React.FC<{
  section?: MaterialMovementCardSectionDTO;
  isCardFullScreen?: boolean;
  showAfternoon?: boolean;
  target: LinkTarget;
}> = ({ section, isCardFullScreen, showAfternoon = false, target }) => {
  if (!section) return null;

  const colClass = (width: string) =>
    `flex-shrink-0 text-right basis-[${width}] max-w-[${width}] px-1 first:border-l border-r border-theme-table-border text-sm md:text-base ${isCardFullScreen ? "md:text-lg" : ""
    }`;

  return (
    <div className="mb-3">
      <Typography
        variant="subtitle1"
        className={`bg-theme-primary border border-theme-table-border text-theme-primary-contrast !font-bold mb-0 ${isCardFullScreen ? "md:!text-2xl md:!py-2" : ""
          }`}
      >
        {serverTerm(section.title)}
      </Typography>

      <div className="flex bg-theme-table-heading text-theme-table-heading-contrast font-bold border-b border-theme-table-border">
        <span className={`${colClass("40%")} truncate`}></span>
        <span title={i18n.t("dashboard:material.measurement")} className={`${colClass("20%")} truncate`}>{i18n.t("dashboard:material.measurement")}</span>
        <span title={i18n.t("dashboard:material.day")} className={`${colClass(showAfternoon ? "10%" : "20%")} truncate`}>{i18n.t("dashboard:material.day")}</span>
        {showAfternoon && <span title={i18n.t("dashboard:material.afternoon")} className={`${colClass("10%")} truncate`}>{i18n.t("dashboard:material.afternoon")}</span>}
        <span title={i18n.t("dashboard:material.night")} className={`${colClass("10%")} truncate`}>{i18n.t("dashboard:material.night")}</span>
        <span title={i18n.t("dashboard:material.both")} className={`${colClass("10%")} truncate`}>{showAfternoon ? i18n.t("dashboard:material.all") : i18n.t("dashboard:material.both")}</span>
      </div>

      {section.rows.map((row, i) => (
        <AccessLink
          key={i}
          target={target}
          linkedClassName={ROW_LINK_CLASSES}
          className={`flex bg-theme-table-row-even text-theme-table-row-contrast border-b border-theme-table-border transition-colors ${row.isBold ? "font-bold border-t" : ""
            }`}
        >
          <span title={serverTerm(row.label)} className={`${colClass("40%")} truncate`}>{serverTerm(row.label)}</span>
          <span className={`${colClass("20%")}`}>{serverTerm(row.measurement)}</span>
          <span className={`${colClass(showAfternoon ? "10%" : "20%")}`}>{formatNumber(row.day)}</span>
          {showAfternoon && (<span className={`${colClass("10%")}`}>{formatNumber(row.afternoon)}</span>)}
          <span className={`${colClass("10%")}`}>{formatNumber(row.night)}</span>
          <span className={`${colClass("10%")}`}>{formatNumber(row.both)}</span>
        </AccessLink>
      ))}
    </div>
  );
};
